/**
 * The public, absolute endpoint URLs we *show* to a user.
 *
 * `REACT_APP_API_URL` is a **request** base: in production it is the relative
 * `/api`, which is exactly what makes the SPA's same-origin `axios` calls work.
 * Concatenating it into text handed to a user produces `/api/endpoint/<slug>` —
 * a string nobody can paste into a webhook field or run through cURL.
 *
 * So every place a URL is displayed, copied, exported or put into a cURL
 * command goes through `buildEndpointUrl()` / `getPublicApiBase()` below, while
 * `axios` keeps using `REQUEST_API_BASE` untouched.
 */

/** Build-time request base: relative in production, absolute in local dev. */
export const REQUEST_API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

/**
 * Optional build-time override for the *displayed* base
 * (`REACT_APP_PUBLIC_API_URL`). Unset everywhere today, so the resolved origin
 * (see `resolvePublicApiBase`) is what the live site uses.
 */
const PUBLIC_API_URL = process.env.REACT_APP_PUBLIC_API_URL;

const withoutTrailingSlashes = (value: string): string => value.replace(/\/+$/, '');

const isAbsoluteUrl = (value: string): boolean => /^[a-z][a-z0-9+.-]*:\/\//i.test(value);

/**
 * The captured sub-path as it belongs on the end of the endpoint URL.
 *
 * `null`/undefined — a request to the bare endpoint, or a row captured before
 * the backend recorded paths — and a bare `/` all mean "nothing to append".
 * The leading slash is added if it is missing so a malformed value cannot
 * produce `/endpoint/abcorders/created`.
 */
export const capturedSubPath = (path?: string | null): string => {
  if (!path || path === '/') {
    return '';
  }
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Resolves the base to *display* an endpoint URL against:
 *
 * 1. `publicApiUrl` when configured — an explicit host override wins outright;
 * 2. an absolute `apiUrl` (local dev, `http://localhost:3000`) used as-is;
 * 3. a relative `apiUrl` (`/api` in production) resolved against the page
 *    origin, which on the live site is `https://whatamisending.dev`, so the
 *    displayed URL becomes `https://whatamisending.dev/api/endpoint/<slug>`
 *    with no extra configuration.
 *
 * A trailing slash never survives, so the caller can always append `/endpoint/…`.
 */
export const resolvePublicApiBase = (
  apiUrl: string,
  publicApiUrl?: string | null,
  origin?: string | null,
): string => {
  const explicit = (publicApiUrl ?? '').trim();
  if (explicit) {
    return withoutTrailingSlashes(explicit);
  }

  const base = (apiUrl ?? '').trim();
  if (!base) {
    return '';
  }
  if (isAbsoluteUrl(base)) {
    return withoutTrailingSlashes(base);
  }

  // No `window` in a non-browser context: hand back the relative base rather
  // than inventing a host.
  const pageOrigin = (
    origin ?? (typeof window !== 'undefined' && window.location ? window.location.origin : '')
  ).trim();
  if (!pageOrigin) {
    return base;
  }

  // `new URL` normalises the join, so a relative base and a trailing slash in
  // the origin both come out as one clean absolute base.
  return withoutTrailingSlashes(new URL(base, pageOrigin).toString());
};

/** The absolute base every displayed endpoint URL hangs off. */
export const getPublicApiBase = (): string =>
  resolvePublicApiBase(REQUEST_API_BASE, PUBLIC_API_URL);

/**
 * The absolute URL a user can copy: `https://<host>/api/endpoint/<slug>` plus
 * the captured sub-path when the request did not hit the bare endpoint.
 */
export const buildEndpointUrl = (slug: string, path?: string | null): string =>
  `${getPublicApiBase()}/endpoint/${slug}${capturedSubPath(path)}`;

/**
 * The slug inside a stored endpoint URL — the last non-empty path segment of
 * `/api/endpoint/<slug>`.
 *
 * Handles a legacy relative value (`/api/endpoint/<slug>`) as well as a
 * malformed one (naive split rather than a throw), because a stored URL is
 * whatever the browser wrote months ago. Two rows therefore compare on the
 * slug alone, whether they were stored absolute or relative.
 */
export const slugFromEndpointUrl = (url: string): string => {
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  } catch (err) {
    const parts = url.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
  }
};
